<template>
  <div class="container mx-auto">
    <h1 class="text-2xl font-bold mb-6">User Settings</h1>
    
    <div class="bg-card rounded-lg shadow p-6 max-w-2xl">
      <div class="flex items-center mb-8">
        <div class="relative">
          <div class="h-24 w-24 rounded-full bg-primary/20 flex items-center justify-center overflow-hidden">
            <span v-if="!userData?.avatar" class="text-4xl font-semibold text-primary">
              {{ userInitials }}
            </span>
            <img v-else :src="userData.avatar" alt="User avatar" class="h-full w-full object-cover" />
          </div>
          <button 
            class="absolute bottom-0 right-0 bg-primary text-primary-foreground rounded-full p-1 shadow-md hover:bg-primary/90 transition-colors"
            title="Change avatar"
          >
            <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
          </button>
        </div>
        <div class="ml-6">
          <h2 class="text-xl font-semibold">{{ userData?.name || 'User Name' }}</h2>
          <p class="text-muted-foreground">{{ userData?.email || 'user@example.com' }}</p>
        </div>
      </div>

      <form @submit.prevent="saveSettings" class="space-y-6">
        <div class="grid grid-cols-1 gap-6 md:grid-cols-2">
          <div>
            <label for="name" class="block text-sm font-medium mb-1">Full Name</label>
            <input 
              type="text" 
              id="name" 
              v-model="form.name" 
              class="w-full px-3 py-2 border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
              placeholder="Your full name"
            >
          </div>
          
          <div>
            <label for="email" class="block text-sm font-medium mb-1">Email Address</label>
            <input 
              type="email" 
              id="email" 
              v-model="form.email" 
              class="w-full px-3 py-2 border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
              placeholder="your.email@example.com"
            >
          </div>
          
          <div>
            <label for="username" class="block text-sm font-medium mb-1">Username</label>
            <input 
              type="text" 
              id="username" 
              v-model="form.username" 
              class="w-full px-3 py-2 border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
              placeholder="username"
            >
          </div>
          
          <div>
            <label for="system_role" class="block text-sm font-medium mb-1">System Role</label>
            <input
              type="text"
              id="system_role"
              :value="formatSystemRole(form.system_role as SystemRole)"
              class="w-full px-3 py-2 border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
              placeholder="Your system role"
              disabled
            >
          </div>
          
          <div>
            <label for="business_role" class="block text-sm font-medium mb-1">Business Role</label>
            <select
              id="business_role"
              v-model="form.business_role"
              class="w-full px-3 py-2 border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
            >
              <option v-for="role in businessRoles" :key="role" :value="role">{{ formatBusinessRole(role) }}</option>
            </select>
          </div>
        </div>
              
        <!-- Success and Error Messages -->
        <div v-if="successMessage || error || passwordError" class="pt-4 border-t border-border">
          <div v-if="successMessage" class="p-3 bg-green-100 text-green-700 rounded-md mb-3">
            {{ successMessage }}
          </div>
          <div v-if="error" class="p-3 bg-red-100 text-red-700 rounded-md mb-3">
            {{ error.message }}
          </div>
          <div v-if="passwordError" class="p-3 bg-red-100 text-red-700 rounded-md mb-3">
            {{ passwordError }}
          </div>
        </div>
        
        <div class="flex justify-end space-x-3">
          <button
            type="button"
            class="px-4 py-2 border border-input rounded-md hover:bg-accent transition-colors"
            @click="resetForm"
          >
            Cancel
          </button>
          <button
            type="submit"
            class="px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors"
            :disabled="loading"
          >
            <span v-if="loading">Saving...</span>
            <span v-else>Save Changes</span>
          </button>
        </div>
      </form>
    </div>
    
    <!-- Personal Data Download Card -->
    <div class="bg-card rounded-lg shadow p-6 max-w-2xl mt-6">
      <h2 class="text-xl font-semibold mb-4">Personal Data</h2>
      <p class="text-muted-foreground mb-4">
        Download a copy of your personal data stored in our system including your profile information and preferences
      </p>
      <button
        @click="downloadPersonalData"
        class="flex items-center px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        :disabled="downloadLoading"
      >
        <svg v-if="!downloadLoading" xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
        </svg>
        <svg v-else class="animate-spin h-5 w-5 mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
          <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
          <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
        </svg>
        <span v-if="downloadLoading">Downloading...</span>
        <span v-else>Download My Data</span>
      </button>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, computed, watch, PropType } from 'vue';
import { useAuth } from '@/composables/useAuth';
import { supabase } from '@/services/supabase';
import type { UserSettingsFormData } from '@/types/user';
import type { Profile } from '@/types/supabase';
import { BusinessRole, formatBusinessRole } from '@/types/businessRole';
import { formatSystemRole, SystemRole } from '@/types/systemRole';

interface UserData {
  name: string;
  email: string;
  username: string;
  role: string;
  system_role: SystemRole;
  business_role: BusinessRole;
  avatar: string | null;
}

export default defineComponent({
  name: 'UserSettings',
  props: {
    userData: {
      type: Object as PropType<UserData>,
      required: true
    }
  },
  emits: ['update-user-data'],
  setup(props, { emit }) {
    const { updateProfile, updatePassword, loading, error } = useAuth();
    const downloadLoading = ref(false);
    
    const form = ref<UserSettingsFormData>({
      name: '',
      email: '',
      username: '',
      role: 'NOT_USED_ROLE',
      system_role: SystemRole.USER,
      business_role: BusinessRole.PLANNER,
      currentPassword: '',
      newPassword: '',
      emailNotifications: true,
      appNotifications: true
    });
    
    const successMessage = ref<string | null>(null);
    const passwordError = ref<string | null>(null);
    
    const userInitials = computed(() => {
      if (!props.userData?.name) return '?';
      return props.userData.name
        .split(' ')
        .map(name => name.charAt(0))
        .join('')
        .toUpperCase();
    });
    
    const businessRoles = Object.values(BusinessRole);
    
    function resetForm() {
      const userBusinessRole = props.userData?.business_role;
      const userSystemRole = props.userData?.system_role;
  
      let businessRole = BusinessRole.PLANNER;
      
      if (userBusinessRole) {
        // Try to find a matching business role
        const matchingRole = Object.values(BusinessRole).find(role =>
          role.toLowerCase() === userBusinessRole.toLowerCase() ||
          role.replace(/\s+/g, '') === userBusinessRole.replace(/\s+/g, '')
        );
        
        if (matchingRole) {
          businessRole = matchingRole;
        }
      }

      let systemRole = SystemRole.USER;
      
      if (userSystemRole) {
        if (userSystemRole === SystemRole.ADMIN || userSystemRole === SystemRole.USER) {
          systemRole = userSystemRole;
        }
      }
      
      const formData = {
        name: props.userData?.name || '',
        email: props.userData?.email || '',
        username: props.userData?.username || '',
        system_role: systemRole,
        business_role: businessRole,
        currentPassword: '',
        newPassword: '',
        emailNotifications: true,
        appNotifications: true,
        role: 'NOT_USED' // Adding this to satisfy the type system
      };
      
      form.value = formData;
    }
    
    // Watch for changes in userData and update form
    watch(() => props.userData, resetForm, { immediate: true });
    
    async function saveSettings() {
        successMessage.value = null;
        passwordError.value = null;
        
        try {
          // Update profile information
          const profileUpdates: Partial<Profile> = {
            name: form.value.name,
            username: form.value.username,
            role: form.value.system_role === SystemRole.ADMIN ? 'Administrator' : 'User', // Map to database field
            business_role: form.value.business_role
          };
          
          // Validate business role before saving
          if (!Object.values(BusinessRole).includes(form.value.business_role as BusinessRole)) {
            throw new Error(`Invalid business role: ${form.value.business_role}`);
          }
          
          // Save profile updates to Supabase
          const updatedProfile = await updateProfile(profileUpdates);
          
          if (!updatedProfile) {
            throw new Error('Failed to update profile');
          }
                    
          // Update password if provided
          if (form.value.currentPassword && form.value.newPassword) {
            try {
              await updatePassword(form.value.currentPassword, form.value.newPassword);
              
              // Clear password fields after successful update
              form.value.currentPassword = '';
              form.value.newPassword = '';
            } catch (err) {
              passwordError.value = err instanceof Error ? err.message : 'Failed to update password';
            }
          }
          
          // Emit event with updated data
          const updatedUserData = {
            ...(props.userData || {}),
            name: form.value.name,
            username: form.value.username,
            system_role: form.value.system_role,
            business_role: form.value.business_role
          };
          
          console.log('Emitting updated user data:', updatedUserData);
          emit('update-user-data', updatedUserData);
          
          successMessage.value = 'Settings saved successfully!';
        } catch (err) {
          console.error('Profile update error');
          error.value = err instanceof Error ? err : new Error('Failed to update profile');
        }
      }
    
    /**
     * Download the user's personal data as a JSON file
     */
    async function downloadPersonalData() {
      if (!props.userData) return;
      
      downloadLoading.value = true;
      
      try {
        // Fetch the user's profile data
        const { data, error: err } = await supabase
          .from('profiles')
          .select('name, username, avatar_url, email, notification_preferences')
          .eq('email', props.userData.email)
          .eq('username', props.userData.username)
          .single();
        
        if (err) throw err;
        
        // Create a JSON blob
        const jsonData = JSON.stringify(data, null, 2);
        const blob = new Blob([jsonData], { type: 'application/json' });
        
        // Create a download link
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = 'personal-data.json';
        
        // Trigger the download
        document.body.appendChild(link);
        link.click();
        
        // Clean up
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      } catch (err) {
        console.error('Error downloading personal data:', err);
        error.value = err instanceof Error ? err : new Error('Failed to download personal data');
      } finally {
        downloadLoading.value = false;
      }
    }
    
    return {
      form,
      loading,
      downloadLoading,
      error,
      passwordError,
      successMessage,
      userInitials,
      businessRoles,
      resetForm,
      saveSettings,
      downloadPersonalData,
      formatBusinessRole,
      formatSystemRole
    };
  }
});
</script>